/**
 * The contact form's rules and its one request, kept out of the component so
 * they can be tested without a DOM.
 *
 * The same limits live in `web/functions/_lib/contact.ts`: the form says what
 * is wrong before sending, the function refuses what the form would have. An
 * address is never part of this file — the owner's inbox is a secret the
 * Pages Function holds (spec §4.3).
 */

export interface ContactInput {
  name: string;
  email: string;
  bioregion: string;
  message: string;
  optIn: boolean;
}

export interface ContactPayload extends ContactInput {
  turnstileToken: string;
  /** Honeypot: a real person never fills it; the function drops what does. */
  website: string;
}

export const LIMITS = {
  name: [1, 120],
  bioregion: [0, 200],
  message: [20, 4000],
} as const;

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export type Validation =
  | { ok: true; input: ContactInput }
  | { ok: false; errors: Partial<Record<keyof ContactInput, string>> };

export function validateContact(raw: ContactInput): Validation {
  const input: ContactInput = {
    name: raw.name.trim(),
    email: raw.email.trim(),
    bioregion: raw.bioregion.trim(),
    message: raw.message.trim(),
    optIn: Boolean(raw.optIn),
  };
  const errors: Partial<Record<keyof ContactInput, string>> = {};
  if (input.name.length < LIMITS.name[0]) errors.name = "Your name, so I know who I'm writing back to.";
  else if (input.name.length > LIMITS.name[1]) errors.name = `Under ${LIMITS.name[1]} characters, please.`;
  if (!EMAIL.test(input.email)) errors.email = "An address I can reply to.";
  if (input.bioregion.length > LIMITS.bioregion[1]) errors.bioregion = `Under ${LIMITS.bioregion[1]} characters, please.`;
  if (input.message.length < LIMITS.message[0]) errors.message = "A few sentences: the place, and what you hope a twin would do for it.";
  else if (input.message.length > LIMITS.message[1]) errors.message = `Under ${LIMITS.message[1]} characters, please.`;
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, input };
}

export type SendResult = "sent" | "rate_limited" | "rejected" | "failed";

/** POST to the Pages Function. The response body is never shown; the status is the verdict. */
export async function submitContact(
  input: ContactInput,
  turnstileToken: string,
  /** Whatever the off-screen `website` field holds — empty for a person. */
  website: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SendResult> {
  const payload: ContactPayload = { ...input, turnstileToken, website };
  try {
    const res = await fetchImpl("/api/contact", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (res.ok) return "sent";
    if (res.status === 429) return "rate_limited";
    if (res.status === 400 || res.status === 403) return "rejected";
    return "failed";
  } catch {
    return "failed";
  }
}
